// src/pages/Apps/AppEdit.tsx

import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { message, Tabs, Card, Button, Space, Descriptions, Typography, Modal, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  getApp,
  // ✅ 移除：resetIOSKey, resetAndroidKey
  getAndroidSHA256,
  addAndroidSHA256,
  deleteAndroidSHA256,
  getProductsByApp
} from '@/api/apps';
import {
  getAllProductsForBinding,
  integrateProductToApp,
  removeProductIntegration
} from '@/api/integrations';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SHA256Form } from '@/components/forms/SHA256Form';
import { getCurrentDeveloperId } from '@/stores/authStore';
import type { AndroidSHA256Certificate, AppPlatformKeyWithSecret } from '@/types/app';
import type { Product } from '@/types/product';
// 导入配套图标
import { ArrowLeftOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
App SDK 管理页面
*/
export const AppEdit = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const developerId = getCurrentDeveloperId();

  // 获取 App 详情
  const appQuery = useQuery({
    queryKey: ['app', developerId, appId],
    queryFn: () => getApp(appId!),
    enabled: !!appId && !!developerId,
    retry: 1,
    staleTime: 0,
  });

  // 获取 Android SHA256 证书
  const sha256Query = useQuery({
    queryKey: ['android-sha256', developerId, appId],
    queryFn: () => getAndroidSHA256(appId!).then(res => res.certificates),
    enabled: !!appId && !!developerId,
    retry: 1,
  });

  // 获取 App 已集成的产品
  const integratedProductsQuery = useQuery({
    queryKey: ['app-integrated-products', developerId, appId],
    queryFn: () => getProductsByApp(appId!),
    enabled: !!appId && !!developerId,
    retry: 1,
  });

  // 获取所有产品（用于选择）
  const allProductsQuery = useQuery({
    queryKey: ['all-products', developerId],
    queryFn: () => getAllProductsForBinding(),
    enabled: !!developerId,
    retry: 1,
  });

  // ✅ 对所有产品按照 created_at 倒序排序（用于可集成产品表格）
  const sortedAllProducts = useMemo(() => {
    if (!allProductsQuery.data) return [];
    return [...allProductsQuery.data].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [allProductsQuery.data]);

  // 构建 allProducts 的 id -> product 映射，用于补全 category
  const allProductsMap = useMemo(() => {
    const map = new Map<string, Product>();
    // ✅ 使用排序后的产品数据构建映射
    (sortedAllProducts || []).forEach(product => {
      map.set(product.id, product);
    });
    return map;
  }, [sortedAllProducts]);

  // ✅ 移除：resetIOSMutation 和 resetAndroidMutation

  // 添加 Android SHA256 证书
  const addCertMutation = useMutation({
    mutationFn: ({ sha256, description }: { sha256: string; description: string }) =>
      addAndroidSHA256(appId!, { sha256_hash: sha256, description }),
    onSuccess: () => {
      message.success('Android SHA256 证书添加成功');
      sha256Query.refetch();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error, '添加失败');
      message.error(errorMessage);
    }
  });

  // 删除 Android SHA256 证书
  const deleteCertMutation = useMutation({
    mutationFn: (certId: string) => deleteAndroidSHA256(appId!, certId),
    onSuccess: () => {
      message.success('Android SHA256 证书删除成功');
      sha256Query.refetch();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error, '删除失败');
      message.error(errorMessage);
    }
  });

  // 集成产品
  const integrateProductMutation = useMutation({
    mutationFn: (productId: string) => integrateProductToApp(appId!, productId),
    onSuccess: () => {
      message.success('产品集成成功');
      integratedProductsQuery.refetch();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error, '产品集成失败');
      message.error(errorMessage);
    }
  });

  // 移除产品集成
  const removeProductIntegrationMutation = useMutation({
    mutationFn: (productId: string) => removeProductIntegration(appId!, productId),
    onSuccess: () => {
      message.success('产品解除集成成功');
      integratedProductsQuery.refetch();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error, '产品解除集成失败');
      message.error(errorMessage);
    }
  });

  if (appQuery.isLoading) {
    return <LoadingSpinner />;
  }

  if (appQuery.error) {
    return <ErrorAlert message="加载失败" description="无法加载 App 详情，请检查网络或稍后重试。" className="mb-8" />;
  }

  const handleBackToList = () => {
    navigate('/apps');
  };

  // ✅ 移除：handleResetIOSKey 和 handleResetAndroidKey 函数

  const handleAddSHA256 = async (sha256: string, description: string) => {
    addCertMutation.mutate({ sha256, description });
  };

  const handleDeleteSHA256 = (certId: string) => {
    Modal.confirm({
      title: '确认删除',
      icon: <WarningOutlined className="text-orange-500" />,
      content: '确定要删除这个 Android SHA256 证书吗？删除后相关应用将无法正常访问。',
      okText: '确定',
      cancelText: '取消',
      onOk: () => deleteCertMutation.mutate(certId),
    });
  };

  const handleIntegrateProduct = (productId: string) => {
    Modal.confirm({
      title: '确认集成',
      content: '确定要将该产品集成到此 App 吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => integrateProductMutation.mutate(productId),
    });
  };

  const handleRemoveProductIntegration = (productId: string) => {
    Modal.confirm({
      title: '确认解除集成',
      content: '确定要将该产品从此 App 解除集成吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => removeProductIntegrationMutation.mutate(productId),
    });
  };

  // SDK 类别映射（增加颜色，统一 Tag）
  const sdkCategoryMap: Record<string, { label: string; color: string }> = {
    smart_home: { label: '智能生活', color: 'blue' },
    wearable: { label: '可穿戴设备', color: 'purple' },
    healthcare: { label: '健康医疗', color: 'green' },
    industrial: { label: '工业 IoT', color: 'orange' },
  };

  // 渠道映射（增加颜色，统一 Tag）
  const channelMap: Record<string, { label: string; color: string }> = {
    official: { label: '官方渠道', color: 'primary' },
    beta: { label: '测试渠道', color: 'warning' },
    enterprise: { label: '企业定制', color: 'success' },
  };

  // 产品集成表格列定义（已集成）
  const integratedProductsColumns: ColumnsType<Product> = [
    {
      title: '产品名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: '产品 ID',
      dataIndex: 'id',
      key: 'id',
      width: 180,
      render: (id) => <span className="font-mono">{id}</span>,
    },
    {
      title: '类别',
      key: 'category',
      render: (_, record) => {
        // 优先使用 record 自带的 category
        if (record.category?.name) {
          return record.category.name;
        }
        // 否则从 allProductsMap 中查找补全
        const fullProduct = allProductsMap.get(record.id);
        return fullProduct?.category?.name || '—';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveProductIntegration(record.id)}
          loading={removeProductIntegrationMutation.isPending && removeProductIntegrationMutation.variables === record.id}
          className="hover:shadow-sm transition-shadow"
        >
          解除
        </Button>
      ),
    },
  ];

  // 可集成产品表格列定义
  const availableProductsColumns: ColumnsType<Product> = [
    {
      title: '产品名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: '产品 ID',
      dataIndex: 'id',
      key: 'id',
      width: 180,
      render: (id) => <span className="font-mono">{id}</span>,
    },
    {
      title: '类别',
      key: 'category',
      render: (_, record) => record.category?.name || '—',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => {
        return new Date(date).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).replace(/\//g, '-');
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        const isAlreadyIntegrated = integratedProductsQuery.data?.some(p => p.id === record.id);
        return (
          <Space size="small">
            {isAlreadyIntegrated ? (
              <Tag color="green">已集成</Tag>
            ) : (
              <Button
                size="small"
                type="primary"
                onClick={() => handleIntegrateProduct(record.id)}
                loading={integrateProductMutation.isPending && integrateProductMutation.variables === record.id}
                className="hover:shadow-sm transition-shadow"
              >
                集成
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  // 获取分类/渠道配置
  const categoryConfig = appQuery.data?.sdk_category
    ? sdkCategoryMap[appQuery.data.sdk_category] || { label: appQuery.data.sdk_category, color: 'gray' }
    : { label: '', color: 'gray' };

  const channelConfig = appQuery.data?.channel
    ? channelMap[appQuery.data.channel] || { label: appQuery.data.channel, color: 'default' }
    : { label: '', color: 'default' };

  const tabsItems = [
    {
      key: 'basic',
      label: '基本信息',
      children: (
        <Card
          className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
          style={{ padding: '0px' } as React.CSSProperties}
        >
          <Descriptions
            column={1}
            bordered={false}
            labelStyle={{ width: '140px', fontWeight: 600, color: '#667085' }}
            contentStyle={{ color: '#1D2129' }}
            style={{
              '--ant-descriptions-item-margin-bottom': '16px',
              fontSize: '15px'
            } as React.CSSProperties}
          >
            <Descriptions.Item label="App 名称">{appQuery.data?.name}</Descriptions.Item>
            <Descriptions.Item label="App ID">{appQuery.data?.id}</Descriptions.Item>
            <Descriptions.Item label="SDK 类别">
              <Tag color={categoryConfig.color}>{categoryConfig.label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="渠道标识符">
              <Tag color={channelConfig.color}>{channelConfig.label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="iOS Bundle ID">
              {appQuery.data?.ios_key?.bundle_id || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Android 包名">
              {appQuery.data?.android_key?.bundle_id || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {appQuery.data?.created_at ? new Date(appQuery.data.created_at).toLocaleString() : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {appQuery.data?.updated_at ? new Date(appQuery.data.updated_at).toLocaleString() : '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'keys',
      label: '密钥管理',
      children: (
        <Card
          className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
          style={{ padding: '0px' } as React.CSSProperties}
        >
          <div className="space-y-8">
            {/* iOS 密钥信息 - ✅ 移除重置按钮 */}
            <Card 
              title="iOS 密钥"
              style={{ 
                '--ant-card-head-font-size': '16px',
                '--ant-card-head-font-weight': 600,
                padding: '0px'
              } as React.CSSProperties}
              className="border-0 shadow-sm"
            >
              <Space direction="vertical" className="w-full gap-4">
                <div className="flex flex-col gap-1">
                  <Text strong className="text-gray-600">App Key:</Text>
                  <Text copyable={{ text: appQuery.data?.ios_key?.app_key || '' }} className="font-mono">
                    {appQuery.data?.ios_key?.app_key || <span className="text-gray-400">—</span>}
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text strong className="text-gray-600">App Secret:</Text>
                  <Text copyable={{ text: (appQuery.data?.ios_key as AppPlatformKeyWithSecret)?.app_secret || '' }} className="font-mono">
                    {(appQuery.data?.ios_key as AppPlatformKeyWithSecret)?.app_secret 
                      ? '••••••••••••••••••••••••••••••••' 
                      : <span className="text-gray-400">—</span>}
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text strong className="text-gray-600">Bundle ID:</Text>
                  <Text copyable={{ text: appQuery.data?.ios_key?.bundle_id || '' }} className="font-mono">
                    {appQuery.data?.ios_key?.bundle_id || <span className="text-gray-400">—</span>}
                  </Text>
                </div>
                {/* ✅ 移除：重置 iOS 密钥按钮 */}
              </Space>
            </Card>

            <br />

            {/* Android 密钥信息 - ✅ 移除重置按钮 */}
            <Card 
              title="Android 密钥"
              style={{ 
                '--ant-card-head-font-size': '16px',
                '--ant-card-head-font-weight': 600,
                padding: '0px'
              } as React.CSSProperties}
              className="border-0 shadow-sm"
            >
              <Space direction="vertical" className="w-full gap-4">
                <div className="flex flex-col gap-1">
                  <Text strong className="text-gray-600">App Key:</Text>
                  <Text copyable={{ text: appQuery.data?.android_key?.app_key || '' }} className="font-mono">
                    {appQuery.data?.android_key?.app_key || <span className="text-gray-400">—</span>}
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text strong className="text-gray-600">App Secret:</Text>
                  <Text copyable={{ text: (appQuery.data?.android_key as AppPlatformKeyWithSecret)?.app_secret || '' }} className="font-mono">
                    {(appQuery.data?.android_key as AppPlatformKeyWithSecret)?.app_secret 
                      ? '••••••••••••••••••••••••••••••••' 
                      : <span className="text-gray-400">—</span>}
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text strong className="text-gray-600">Package Name:</Text>
                  <Text copyable={{ text: appQuery.data?.android_key?.bundle_id || '' }} className="font-mono">
                    {appQuery.data?.android_key?.bundle_id || <span className="text-gray-400">—</span>}
                  </Text>
                </div>
                {/* ✅ 移除：重置 Android 密钥按钮 */}
              </Space>
            </Card>
          </div>
        </Card>
      ),
    },
    {
      key: 'android-certificates',
      label: 'Android 证书',
      children: (
        <Card 
          className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
          style={{ padding: '0px' } as React.CSSProperties}
        >
          <div className="mb-8">
            <SHA256Form onSubmit={handleAddSHA256} />
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <span>已添加的证书</span>
              <Tag color="blue">{sha256Query.data?.length || 0}</Tag>
            </h3>
            {sha256Query.isLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sha256Query.data?.map((cert: AndroidSHA256Certificate) => (
                  <Card 
                    key={cert.id} 
                    className="border-0 shadow-sm hover:shadow-md transition-shadow"
                    style={{ 
                      padding: '16px',
                      '--ant-card-border-radius': '8px'
                    } as React.CSSProperties}
                    extra={
                      <Button 
                        size="small" 
                        danger
                        icon={<DeleteOutlined className="text-xs" />}
                        onClick={() => handleDeleteSHA256(cert.id)}
                        loading={deleteCertMutation.isPending && deleteCertMutation.variables === cert.id}
                        className="hover:shadow-sm"
                      >
                        删除
                      </Button>
                    }
                  >
                    <div className="font-mono text-sm break-all mb-2 text-blue-600">{cert.sha256_hash}</div>
                    <div className="text-gray-700 text-sm mb-1">{cert.description}</div>
                    <div className="text-gray-400 text-xs">
                      创建于：{new Date(cert.created_at).toLocaleString().split(' ')[0]}
                    </div>
                  </Card>
                ))}
                {(!sha256Query.data || sha256Query.data.length === 0) && (
                  <div className="col-span-2 text-center text-gray-500 py-12 bg-gray-50 rounded-lg">
                    暂无 Android SHA256 证书，添加后即可支持相关 Android 应用集成。
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      ),
    },
    {
      key: 'product-integration',
      label: '产品集成',
      children: (
        <Card 
          className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
          style={{ padding: '0px' } as React.CSSProperties}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-6">已集成的产品</h3>
          <Table
            columns={integratedProductsColumns}
            dataSource={integratedProductsQuery.data || []}
            rowKey="id"
            loading={integratedProductsQuery.isLoading}
            pagination={{ pageSize: 10, showTotal: (total) => `共 ${total} 个` }}
            className="mb-8"
            bordered={false}
            size="middle"
            scroll={{ x: 'max-content' }}
            style={{
              '--ant-table-row-hover-bg': '#f5faff',
              '--ant-table-header-text-color': '#667085'
            } as React.CSSProperties}
            locale={{ emptyText: '暂无已集成的产品，快去下方选择产品集成吧。' }}
          />

          <h3 className="text-lg font-semibold text-gray-800 mb-6">可集成的产品</h3>
          <Table
            columns={availableProductsColumns}
            // ✅ 使用排序后的产品数据
            dataSource={sortedAllProducts || []}
            rowKey="id"
            loading={allProductsQuery.isLoading || integrateProductMutation.isPending}
            pagination={{ pageSize: 10, showTotal: (total) => `共 ${total} 个` }}
            bordered={false}
            size="middle"
            scroll={{ x: 'max-content' }}
            style={{
              '--ant-table-row-hover-bg': '#f5faff',
              '--ant-table-header-text-color': '#667085'
            } as React.CSSProperties}
          />
        </Card>
      ),
    },
  ];

  return (
    <div className="w-full space-y-8">
      {/* 页面标题 + 操作区 - 适配小屏 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            App SDK 管理 - {appQuery.data?.name || '加载中...'}
          </h1>
          <p className="text-gray-600 mt-1">
            编辑 App 基础信息、密钥、证书及产品集成配置
          </p>
        </div>
        <Button
          onClick={handleBackToList}
          icon={<ArrowLeftOutlined />}
          className="hover:bg-gray-50 transition-colors"
        >
          返回列表
        </Button>
      </div>

      {appQuery.data && (
        <Tabs 
          defaultActiveKey="basic" 
          items={tabsItems}
          destroyInactiveTabPane
          className="ant-tabs-card ant-tabs-top"
          style={{
            '--ant-tabs-card-border-color': 'transparent',
            '--ant-tabs-tab-active-bg': '#fff',
            '--ant-tabs-tab-active-color': '#165DFF',
            '--ant-tabs-tab-hover-bg': '#f5faff',
            '--ant-tabs-border-radius': '8px'
          } as React.CSSProperties}
        />
      )}
    </div>
  );
};

// 辅助函数：获取错误消息
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const errorWithResponse = error as { response?: { data?: { message?: string } } };
    return errorWithResponse.response?.data?.message || defaultMessage;
  }
  return String(error) || defaultMessage;
}